import { create } from "zustand";
import { User as SupabaseUser } from "@supabase/supabase-js";
import type { User } from "@/app/interfaces";
import { supabase } from "@/db/supabaseclient";
import { getUsernameFromEmail } from "@/utils/utils";

interface AuthState {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;

  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  fetchUser: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

function mapUser(u: SupabaseUser | null): User | null {
  if (!u || !u.email) return null;
  return {
    id: u.id,
    email: u.email,
    deviceId: u.user_metadata?.deviceId,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAdmin: false,
  loading: true,

  setUser: (user) =>
    set({
      user,
      isAdmin: getUsernameFromEmail(user?.email) === "admin",
    }),

  setLoading: (loading) => set({ loading }),

  fetchUser: async () => {
    set({ loading: true });
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) {
      console.error("fetchUser error:", error);
      set({ user: null, isAdmin: false, loading: false });
      return;
    }
    const appUser = mapUser(session?.user ?? null);
    set({
      user: appUser,
      isAdmin: getUsernameFromEmail(appUser?.email) === "admin",
      loading: false,
    });
  },

  login: async (username, password) => {
    set({ loading: true });
    const email = `${username}@lvmeh.ee`;
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      set({ loading: false });
      throw new Error(error.message);
    }
    await useAuthStore.getState().fetchUser();
  },

  logout: async () => {
    set({ loading: true });
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("logout error:", error);
    }
    set({ user: null, isAdmin: false, loading: false });
  },
}));
